import { useEffect, useState } from 'react';
import { crypto as cv } from '@photo-vault/shared';
import { getCipher, getThumb, putCipher, putThumb } from './mediaCache.js';
import { getDecryptPool } from './decryptPool.js';
import { generateVideoThumbnail } from './videoThumb.js';

interface Entry {
  url: string;
  refCount: number;
}

// Granular phases drive the per-tile placeholder copy in the grid. The grid
// uses these to surface what's actually happening (download / decrypt /
// thumbnail) instead of a static skeleton on large albums where the full
// pipeline can take seconds per tile.
export type LoadingPhase =
  | 'awaiting-url' // caller hasn't passed the presigned URL yet (batch in flight)
  | 'cache-thumb' // looking up encrypted thumb in IDB (cheap path)
  | 'cache-cipher' // looking up the full cipher in IDB (after thumb miss)
  | 'fetching' // GET on the presigned URL (S3)
  | 'decrypting' // AES-GCM in the worker pool
  | 'thumbnailing'; // generating the encrypted thumb after a full decrypt

interface Source {
  state: 'loading' | 'ready' | 'error' | 'no-preview';
  phase?: LoadingPhase;
  blobUrl?: string;
  error?: string;
}

export interface MediaSourceOptions {
  /** Try the cached encrypted thumbnail first. On miss, falls back to the
   *  full-resolution decrypt path (which also seeds the thumb cache for
   *  future visits). Used by the grid to keep dozens of tiles cheap on
   *  reload — the first visit still pays the full-decrypt cost once. */
  thumb?: boolean;
  /** Original mime type (from decryptedMeta). Required to drive
   *  image-only thumbnail generation in the worker; ignored otherwise. */
  mimeType?: string;
}

type PhaseListener = (phase: LoadingPhase) => void;

// Two ref-counted caches: one for full-resolution blobs, one for thumbs.
// Keeping them separate lets the grid hold thumb refs independently of the
// viewer's full refs without prematurely revoking either.
const fullCache = new Map<string, Entry>();
const thumbCache = new Map<string, Entry>();
// In-flight is keyed on the full-decrypt pipeline only — that's the
// expensive path and the one we deduplicate aggressively. Thumb-from-IDB is
// already cheap enough that double-decoding occasionally is fine.
const inflightFull = new Map<string, Promise<string>>();
// Dedup the lazy video-thumb pipeline (download → decrypt → frame extract →
// re-encrypt). Multiple tiles for the same id (or grid + viewer racing on
// open) share the same task instead of fetching the video twice. The shared
// promise resolves to the *plaintext* thumb blob; each waiter creates its
// own object URL so refcount/revoke stays one-to-one.
const inflightVideoThumb = new Map<string, Promise<Blob | null>>();

// Cap on parallel video-thumb generations. Videos are large; without a cap a
// 50-tile album scroll would saturate the user's bandwidth and IDB writes.
// Two at a time keeps the network busy without starving image tiles.
const VIDEO_THUMB_CONCURRENCY = 2;
let videoThumbActive = 0;
const videoThumbQueue: Array<() => void> = [];

async function withVideoThumbSlot<T>(fn: () => Promise<T>): Promise<T> {
  while (videoThumbActive >= VIDEO_THUMB_CONCURRENCY) {
    await new Promise<void>((resolve) => videoThumbQueue.push(resolve));
  }
  videoThumbActive += 1;
  try {
    return await fn();
  } finally {
    videoThumbActive -= 1;
    const next = videoThumbQueue.shift();
    if (next) next();
  }
}

async function fetchCipher(
  id: string,
  url: string,
  onPhase?: PhaseListener,
  options: { cache?: boolean } = {},
): Promise<Uint8Array> {
  onPhase?.('cache-cipher');
  const cached = await getCipher(id);
  if (cached) return cached;
  onPhase?.('fetching');
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const bytes = new Uint8Array(await res.arrayBuffer());
  // Skipping the cipher cache for video-thumb generation: the file might be
  // hundreds of MB and we'd burn the 500 MB cipher quota on a single video
  // the user may never open. The thumb itself goes into the (much smaller)
  // thumb store and that's what subsequent visits read from.
  if (options.cache !== false) void putCipher(id, bytes);
  return bytes;
}

async function performFullDecrypt(
  id: string,
  url: string,
  albumKey: CryptoKey,
  mimeType: string | undefined,
  onPhase?: PhaseListener,
): Promise<string> {
  const cipher = await fetchCipher(id, url, onPhase);
  const pool = getDecryptPool();
  let plainBytes: Uint8Array;
  if (mimeType && mimeType.startsWith('image/')) {
    onPhase?.('decrypting');
    const r = await pool.decryptWithThumbnail(cipher, albumKey, mimeType);
    plainBytes = r.bytes;
    if (r.thumb) {
      onPhase?.('thumbnailing');
      void putThumb(id, r.thumb.encryptedBytes);
    }
  } else {
    onPhase?.('decrypting');
    const r = await pool.decrypt(cipher, albumKey);
    plainBytes = r.bytes;
    if (mimeType && mimeType.startsWith('video/')) {
      // Fire-and-forget: derive a JPEG thumb from the first frame on the
      // main thread, re-encrypt it under K_album and seed the thumb cache so
      // the next grid render can show a real preview instead of the play
      // overlay placeholder. Doesn't block the viewer's render.
      onPhase?.('thumbnailing');
      void deriveAndStoreVideoThumb(id, plainBytes, mimeType, albumKey);
    }
  }
  const blob = new Blob([plainBytes as BlobPart], mimeType ? { type: mimeType } : {});
  return URL.createObjectURL(blob);
}

// Same as the lazy grid pipeline below, but reuses bytes the viewer already
// decrypted — we don't redownload the video just to derive a frame. Output
// goes into IDB via putThumb so the next grid render hits the cheap path.
async function deriveAndStoreVideoThumb(
  id: string,
  bytes: Uint8Array,
  mimeType: string,
  albumKey: CryptoKey,
): Promise<void> {
  try {
    const thumbBlob = await generateVideoThumbnail(bytes, mimeType);
    if (!thumbBlob) return;
    const packed = await cv.encryptPhoto(thumbBlob, albumKey);
    await putThumb(id, new TextEncoder().encode(packed));
  } catch {
    // Best-effort — a missing thumb just means the grid keeps showing the
    // play-icon placeholder for this video. Nothing else relies on it.
  }
}

// Lazy video-thumb pipeline used by the grid: download cipher → decrypt in
// the worker → extract first frame on the main thread → re-encrypt with
// K_album → persist in IDB → return the plaintext thumb blob. Returns null
// when frame extraction fails (codec the browser can't decode); the caller
// renders a play-icon placeholder in that case.
async function generateLazyVideoThumb(
  id: string,
  url: string,
  albumKey: CryptoKey,
  mimeType: string,
  onPhase?: PhaseListener,
): Promise<Blob | null> {
  return withVideoThumbSlot(async () => {
    // Re-check cache under the slot — another tile (or the viewer) may have
    // populated IDB while we were queued. Cheap and avoids a redundant fetch.
    const cached = await getThumb(id);
    if (cached) {
      onPhase?.('decrypting');
      const r = await getDecryptPool().decrypt(cached, albumKey);
      return new Blob([r.bytes as BlobPart], { type: 'image/jpeg' });
    }
    const cipher = await fetchCipher(id, url, onPhase, { cache: false });
    onPhase?.('decrypting');
    const r = await getDecryptPool().decrypt(cipher, albumKey);
    onPhase?.('thumbnailing');
    const thumbBlob = await generateVideoThumbnail(r.bytes, mimeType);
    if (!thumbBlob) return null;
    const packed = await cv.encryptPhoto(thumbBlob, albumKey);
    await putThumb(id, new TextEncoder().encode(packed));
    return thumbBlob;
  });
}

async function performThumbDecrypt(
  id: string,
  albumKey: CryptoKey,
  onPhase?: PhaseListener,
): Promise<string | null> {
  onPhase?.('cache-thumb');
  const cached = await getThumb(id);
  if (!cached) return null;
  onPhase?.('decrypting');
  const pool = getDecryptPool();
  const r = await pool.decrypt(cached, albumKey);
  const blob = new Blob([r.bytes as BlobPart], { type: 'image/jpeg' });
  return URL.createObjectURL(blob);
}

function bumpFull(id: string, url: string): void {
  const hit = fullCache.get(id);
  if (hit) {
    hit.refCount += 1;
    return;
  }
  fullCache.set(id, { url, refCount: 1 });
}

function acquireFull(
  id: string,
  url: string,
  albumKey: CryptoKey,
  mimeType: string | undefined,
  onPhase?: PhaseListener,
): Promise<string> {
  const hit = fullCache.get(id);
  if (hit) {
    hit.refCount += 1;
    return Promise.resolve(hit.url);
  }
  const existing = inflightFull.get(id);
  if (existing) {
    return existing.then((blobUrl) => {
      bumpFull(id, blobUrl);
      return blobUrl;
    });
  }
  const p = performFullDecrypt(id, url, albumKey, mimeType, onPhase).then((blobUrl) => {
    fullCache.set(id, { url: blobUrl, refCount: 1 });
    inflightFull.delete(id);
    return blobUrl;
  });
  inflightFull.set(id, p);
  // Caller is the first ref; subsequent waiters bump in their .then handler.
  return p.catch((err) => {
    inflightFull.delete(id);
    throw err;
  });
}

async function acquireThumb(
  id: string,
  url: string,
  albumKey: CryptoKey,
  mimeType: string | undefined,
  onPhase?: PhaseListener,
): Promise<{ kind: 'thumb' | 'full'; url: string } | { kind: 'no-preview' }> {
  const hit = thumbCache.get(id);
  if (hit) {
    hit.refCount += 1;
    return { kind: 'thumb', url: hit.url };
  }
  const blobUrl = await performThumbDecrypt(id, albumKey, onPhase);
  if (blobUrl) {
    const existing = thumbCache.get(id);
    if (existing) {
      // Race: another acquire populated the cache while we were decrypting.
      URL.revokeObjectURL(blobUrl);
      existing.refCount += 1;
      return { kind: 'thumb', url: existing.url };
    }
    thumbCache.set(id, { url: blobUrl, refCount: 1 });
    return { kind: 'thumb', url: blobUrl };
  }
  // Videos: don't fall back to the in-memory full cache (a 500 MB blob URL
  // per tile would be madness). Instead trigger the dedicated lazy pipeline
  // — bounded concurrency, no cipher caching — and either seat the result
  // into thumbCache or surface 'no-preview' if frame extraction failed.
  if (mimeType && mimeType.startsWith('video/')) {
    let task = inflightVideoThumb.get(id);
    if (!task) {
      task = generateLazyVideoThumb(id, url, albumKey, mimeType, onPhase).finally(() => {
        inflightVideoThumb.delete(id);
      });
      inflightVideoThumb.set(id, task);
    }
    const generatedBlob = await task;
    if (!generatedBlob) return { kind: 'no-preview' };
    const existing = thumbCache.get(id);
    if (existing) {
      existing.refCount += 1;
      return { kind: 'thumb', url: existing.url };
    }
    // Each waiter creates its own object URL — refcount/revoke stays
    // one-to-one with the in-memory thumbCache entry.
    const blobUrl = URL.createObjectURL(generatedBlob);
    thumbCache.set(id, { url: blobUrl, refCount: 1 });
    return { kind: 'thumb', url: blobUrl };
  }
  const fullUrl = await acquireFull(id, url, albumKey, mimeType, onPhase);
  return { kind: 'full', url: fullUrl };
}

function release(id: string, kind: 'thumb' | 'full'): void {
  const cache = kind === 'thumb' ? thumbCache : fullCache;
  const entry = cache.get(id);
  if (!entry) return;
  entry.refCount -= 1;
  if (entry.refCount <= 0) {
    URL.revokeObjectURL(entry.url);
    cache.delete(id);
  }
}

// Subscribe to a single decrypted media source. The hook acquires on mount,
// releases on unmount or when the (id, url) pair changes. Callers may pass
// url=null while the presigned-batch chunk for this id is still in flight;
// the hook stays in `awaiting-url` until url becomes a string.
export function useMediaSource(
  id: string | null,
  url: string | null,
  albumKey: CryptoKey | null,
  options?: MediaSourceOptions,
): Source {
  const [source, setSource] = useState<Source>({ state: 'loading', phase: 'awaiting-url' });
  const wantThumb = options?.thumb === true;
  const mimeType = options?.mimeType;

  useEffect(() => {
    if (!id || !albumKey) {
      setSource({ state: 'loading', phase: 'awaiting-url' });
      return;
    }
    if (!url) {
      setSource({ state: 'loading', phase: 'awaiting-url' });
      return;
    }
    let cancelled = false;
    let acquiredKind: 'thumb' | 'full' | null = null;
    setSource({ state: 'loading', phase: wantThumb ? 'cache-thumb' : 'cache-cipher' });

    const onPhase: PhaseListener = (phase) => {
      if (cancelled) return;
      setSource({ state: 'loading', phase });
    };

    const promise = wantThumb
      ? acquireThumb(id, url, albumKey, mimeType, onPhase)
      : acquireFull(id, url, albumKey, mimeType, onPhase).then((u) => ({
          kind: 'full' as const,
          url: u,
        }));

    promise
      .then((res) => {
        if (cancelled) {
          if (res.kind !== 'no-preview') release(id, res.kind);
          return;
        }
        if (res.kind === 'no-preview') {
          setSource({ state: 'no-preview' });
          return;
        }
        acquiredKind = res.kind;
        setSource({ state: 'ready', blobUrl: res.url });
      })
      .catch((err) => {
        if (cancelled) return;
        setSource({
          state: 'error',
          error: err instanceof Error ? err.message : 'errore',
        });
      });
    return () => {
      cancelled = true;
      if (acquiredKind) release(id, acquiredKind);
    };
  }, [id, url, albumKey, wantThumb, mimeType]);

  return source;
}

// Fire-and-forget prefetch: keeps the entry alive only briefly so the next
// real subscription finds it warm. Without releasing, we'd leak.
export function prefetchMedia(id: string, url: string, albumKey: CryptoKey, mimeType?: string) {
  void acquireFull(id, url, albumKey, mimeType).then(() => {
    // Hold for ~30s — long enough to cover the user's swipe/click.
    setTimeout(() => release(id, 'full'), 30_000);
  });
}
